import { Link } from "react-router-dom";
import LogoText from "@/components/LogoText";
import { useLocale, useTranslation } from "@/i18n";
import certBadge9001 from "@/assets/ei-9001-certification.png";
import certBadge19011 from "@/assets/ei-19011-certification.png";
import certBadge360 from "@/assets/ei-360-certification.png";
import certBadgeTrust from "@/assets/ei-trust-certification.png";

const certifications = [
  { src: certBadge9001, code: "EI-9001", label: "Internal Audit Framework — inspiré des principes ISO 9001" },
  { src: certBadge19011, code: "EI-19011", label: "Audit Methodology Protocol — inspiré des lignes directrices ISO 19011" },
  { src: certBadge360, code: "EI-360", label: "Corporate Immersion Assessment — vision 360° de l'expérience terrain" },
  { src: certBadgeTrust, code: "EI-TRUST", label: "Confidential Audit Standard — confidentialité, intégrité, NDA" },
];

const WHATSAPP_NUMBER = "33689605897";

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
  </svg>
);

const Footer = () => {
  const { localePath } = useLocale();
  const t = useTranslation();

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t.whatsapp.message)}`;

  const footerNav = [
    { label: t.nav.home, href: "/" },
    { label: t.nav.about, href: "/a-propos" },
    { label: t.nav.services, href: "/services" },
    { label: t.nav.methodology, href: "/methodologie" },
    { label: t.nav.employeMystere, href: "/employe-mystere" },
    { label: t.nav.cities, href: "/audit-mystere" },
    { label: t.nav.blog, href: "/blog" },
    { label: t.nav.faq, href: "/faq" },
    { label: t.nav.contact, href: "/contact" },
  ];

  const cityLinks = [
    { label: "Paris", href: "/audit-mystere/paris" },
    { label: "Lyon", href: "/audit-mystere/lyon" },
    { label: "Nice", href: "/audit-mystere/nice" },
    { label: "Cannes", href: "/audit-mystere/cannes" },
    { label: "Monaco", href: "/audit-mystere/monaco" },
    { label: "Bordeaux", href: "/audit-mystere/bordeaux" },
    { label: "Courchevel", href: "/audit-mystere/courchevel" },
    { label: "Genève", href: "/audit-mystere/geneve" },
    { label: "Zurich", href: "/audit-mystere/zurich" },
    { label: "Luxembourg", href: "/audit-mystere/luxembourg" },
    { label: t.footer.allCities, href: "/audit-mystere" },
  ];

  return (
    <footer aria-label="Pied de page" className="bg-background text-foreground border-t border-border">
      <div className="container mx-auto px-4 py-10 md:py-12">
        <div className="grid md:grid-cols-12 gap-8 md:gap-10">
          {/* Brand + certification */}
          <div className="md:col-span-4 flex flex-col gap-5">
            <LogoText size="md" />
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
            <div className="pt-2">
              <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-3">Cabinet certifié</p>
              <div className="grid grid-cols-4 gap-2">
                {certifications.map((c) => (
                  <div key={c.code} className="flex flex-col items-center text-center" title={c.label}>
                    <img
                      src={c.src}
                      alt={`Certification ${c.code} — ${c.label}`}
                      loading="lazy"
                      className="w-14 h-14 object-contain"
                    />
                    <span className="text-[10px] text-muted-foreground mt-1 font-medium">{c.code}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <h2 className="font-display text-sm font-semibold text-primary mb-3 tracking-wider uppercase">{t.footer.navigation}</h2>
            <div className="flex flex-col">
              {footerNav.map((l) => (
                <Link key={l.href} to={localePath(l.href)} className="text-sm text-muted-foreground hover:text-primary transition-colors py-1">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Cities — 2 columns */}
          <div className="md:col-span-3">
            <h2 className="font-display text-sm font-semibold text-primary mb-3 tracking-wider uppercase">{t.footer.ourCities}</h2>
            <div className="grid grid-cols-2 gap-x-4">
              {cityLinks.map((l) => (
                <Link key={l.href} to={localePath(l.href)} className="text-sm text-muted-foreground hover:text-primary transition-colors py-1">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="md:col-span-3">
            <h2 className="font-display text-sm font-semibold text-primary mb-3 tracking-wider uppercase">{t.footer.contactTitle}</h2>
            <a href="mailto:contact&#64;employeincognito.com" className="text-sm text-muted-foreground hover:text-primary transition-colors block py-1">
              {"contact" + String.fromCharCode(64) + "employeincognito.com"}
            </a>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-muted-foreground block py-1 hover:text-primary transition-colors">
              +33 6 89 60 58 97 {t.footer.whatsapp}
            </a>
            <a
              href="https://www.instagram.com/employeincognito/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground mt-2 hover:text-primary transition-colors"
              aria-label="Instagram"
            >
              <InstagramIcon />
              @employeincognito
            </a>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              {t.footer.motto}
            </p>
          </div>
        </div>

        <div className="gold-line mt-8 mb-4" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} {t.footer.rights}</p>
          <div className="flex gap-4">
            <Link to={localePath("/mentions-legales")} className="hover:text-primary transition-colors py-1">{t.footer.legal}</Link>
            <Link to={localePath("/politique-de-confidentialite")} className="hover:text-primary transition-colors py-1">{t.footer.privacy}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
